/**
 * NOLAS 2026, application multi evenement e.labriquerie.net.
 * src/i18n/fr.cvm.ts
 *
 * Messages du module CV Matching (cahier des charges app/docs/CDC-CV-MATCHING.md).
 * Vague 0 : le choix à trois niveaux de qui peut voir le CV, à l'inscription
 * et dans Mon profil, et la section de réglages premium. Préfixe `cvm.`,
 * fusionné dans fr.ts comme fr.offres.ts.
 *
 * Le nom et le type de l'événement viennent des jetons d'identité
 * ({eventTypeOf} donne « du salon », « des portes ouvertes »...) : rien n'est
 * écrit en dur, et le mot atelier n'apparaît pas.
 *
 * Mikael - chef de projet - 2026-09-20
 */
export const messagesCvmFr: Record<string, string> = {
  'settingsAdmin.section.premium': 'Module premium (CV Matching)',

  'cvm.visibilite.titre': 'Qui peut voir mon CV ?',
  'cvm.visibilite.intro':
    'Votre choix permet aux entreprises couvertes de consulter votre CV et de vous contacter avant, pendant et après l’événement. Vous pouvez le changer à tout moment dans Mon profil.',
  'cvm.visibilite.introProfil':
    'Les entreprises couvertes par votre choix peuvent consulter votre CV et vous contacter avant, pendant et après l’événement. Chaque consultation reste inscrite dans votre journal.',
  'cvm.visibilite.scanned': 'Seules les entreprises qui scannent mon badge',
  'cvm.visibilite.scanned.aide':
    'Une entreprise voit votre CV quand elle scanne votre badge sur son stand, ou quand vous lui envoyez votre candidature.',
  'cvm.visibilite.premium': 'Les entreprises premium {eventTypeOf}',
  'cvm.visibilite.premium.aide':
    'En plus des entreprises qui vous scannent, les entreprises premium {eventTypeOf} peuvent chercher votre CV et vous proposer un entretien.',
  'cvm.visibilite.all': 'Toutes les entreprises exposantes',
  'cvm.visibilite.all.aide':
    'Toute entreprise exposante {eventTypeOf} peut consulter votre CV, même sans vous avoir rencontré.',
  'cvm.visibilite.mineur':
    'Compte mineur sans accord parental enregistré : seules les entreprises qui scannent votre badge peuvent voir votre CV. L’organisation peut lever cette règle avec l’accord de votre famille.',
  'cvm.visibilite.dernierChoix': 'Dernier choix enregistré le {date}',
  'cvm.visibilite.aucunChoix': 'Aucun choix enregistré : le niveau le plus protecteur s’applique.',
  'cvm.visibilite.enregistrer': 'Enregistrer mon choix',
  'cvm.visibilite.enregistre': 'Votre choix de visibilité est enregistré.',
  'cvm.visibilite.erreur': 'Votre choix n’a pas pu être enregistré. Réessayez.',
  'cvm.visibilite.verrouille':
    'Le niveau appliqué est « {niveau} », quel que soit votre choix.',

  // ── Vague 1 : premium (organisation) ─────────────────────────────────
  'adminNav.premiumSettings': 'Premium : réglages et salles',
  'acces.titre.adminPremiumSettings': 'Premium : réglages et salles',
  'acces.titre.exposantRecruteurs': 'Mes recruteurs',

  'premium.colonne': 'Premium',
  'premium.compteur': 'Premium : {actifs} / {plafond}',
  'premium.compteur.aide': 'Entreprises premium sur le plafond réglé dans Premium : réglages et salles.',
  'premium.statut.actif': 'Premium',
  'premium.statut.demande': 'Demande en attente',
  'premium.statut.refuse': 'Demande refusée',
  'premium.statut.retire': 'Premium retiré',
  'premium.statut.aucun': 'Non',
  'premium.action.activer': 'Passer premium',
  'premium.action.valider': 'Valider la demande',
  'premium.action.refuser': 'Refuser',
  'premium.action.retirer': 'Retirer le premium',
  'premium.action.plafond': 'Plafond atteint',
  'premium.demandeLe': 'Demande du {date}',
  'premium.toast.active': '{societe} est maintenant premium.',
  'premium.toast.refuse': 'La demande de {societe} est refusée.',
  'premium.toast.retire': 'Le premium de {societe} est retiré.',
  'premium.toast.plafond': 'Plafond atteint ({actifs} / {plafond}) : relevez-le dans les réglages premium ou retirez une entreprise.',
  'premium.toast.erreur': 'L’action premium n’a pas abouti. Réessayez.',
  'premium.indisponible.type':
    'Le module premium n’existe que sur un événement de type salon (recrutement et emploi). Cet événement est d’un autre type.',
  'premium.indisponible.desactive':
    'Le module premium est désactivé sur cette instance. Activez-le dans Premium : réglages et salles.',

  // ── Vague 1 : encart Passer premium (exposant) ───────────────────────
  'premium.encart.titre': 'Passer premium',
  'premium.encart.intro':
    'Le premium ouvre à votre entreprise la recherche de CV parmi les candidats qui l’acceptent, des créneaux d’entretien avec une salle attribuée, et des comptes recruteurs pour votre équipe.',
  'premium.encart.demander': 'Demander le premium',
  'premium.encart.envoyee': 'Demande envoyée le {date}. L’organisation vous répondra.',
  'premium.encart.envoyeeSansDate': 'Demande envoyée. L’organisation vous répondra.',
  'premium.encart.actif': 'Votre entreprise est premium.',
  'premium.encart.actif.recruteurs': 'Gérer mes recruteurs',
  'premium.encart.refuse': 'Votre demande a été refusée par l’organisation. Vous pouvez la renouveler.',
  'premium.encart.retire': 'Le premium de votre entreprise a été retiré par l’organisation. Vous pouvez le redemander.',
  'premium.encart.ferme': 'Les demandes ne sont pas ouvertes : adressez-vous à l’organisation.',
  'premium.encart.mereSeule': 'Seul le compte principal de l’entreprise peut demander le premium.',
  'premium.encart.toast.ok': 'Votre demande de premium est envoyée.',
  'premium.encart.toast.erreur': 'La demande n’a pas pu être envoyée. Réessayez.',

  // ── Vague 1 : réglages (organisation) ────────────────────────────────
  'premium.reglages.titre': 'Premium : réglages et salles',
  'premium.reglages.sousTitre':
    'Le module premium (CV Matching et entretiens) : plafonds, plage horaire des entretiens, salles, aperçu de la grille des créneaux.',
  'premium.reglages.chargement': 'Chargement des réglages.',
  'premium.reglages.enregistrer': 'Enregistrer',
  'premium.reglages.enregistre': 'Réglages premium enregistrés.',
  'premium.reglages.erreur': 'Les réglages n’ont pas pu être enregistrés.',
  'premium.reglages.etat': 'État du module',
  'premium.reglages.etat.disponible': 'Module disponible : événement de type salon, module activé.',
  'premium.reglages.etat.type': 'Module indisponible : cet événement n’est pas un salon (type « {type} »). L’activation est refusée.',
  'premium.reglages.etat.desactive': 'Module désactivé : événement de type salon, activez-le ci-dessous.',
  'premium.reglages.enabled': 'Activer le module premium',
  'premium.reglages.enabled.aide': 'Ne s’active que sur un événement de type salon.',
  'premium.reglages.plafonds': 'Plafonds',
  'premium.reglages.maxExhibitors': 'Entreprises premium (plafond)',
  'premium.reglages.maxRecruiters': 'Recruteurs par entreprise (compte principal compris)',
  'premium.reglages.maxInterviews': 'Entretiens par entreprise, tous recruteurs confondus',
  'premium.reglages.requestEnabled': 'Les exposants peuvent demander le premium',
  'premium.reglages.candidateCanRequest': 'Un candidat peut demander un entretien (réservé, décision ultérieure)',
  'premium.reglages.horaires': 'Créneaux d’entretien',
  'premium.reglages.slotsStart': 'Premier créneau',
  'premium.reglages.slotsEnd': 'Fin de la plage (le dernier créneau doit être terminé)',
  'premium.reglages.slotMinutes': 'Durée d’un entretien (minutes)',
  'premium.reglages.bufferMinutes': 'Battement entre deux entretiens (minutes)',
  'premium.reglages.responseHours': 'Délai de réponse du candidat (heures)',
  'premium.reglages.reminderMinutes': 'Rappel avant l’entretien (minutes)',
  'premium.reglages.apercu': 'Aperçu de la grille',
  'premium.reglages.apercu.aide':
    'Calculée à partir des réglages ci-dessus, par recruteur. {nombre, plural, =0 {Aucun créneau : vérifiez la plage et la durée.} one {# créneau.} other {# créneaux.}}',
  'premium.reglages.apercu.creneau': '{debut} à {fin}',
  'premium.reglages.regenerer': 'Régénérer les créneaux',
  'premium.reglages.regenerer.aide':
    'Après un changement de plage ou de durée, applique la nouvelle grille à tous les recruteurs des entreprises premium. Les créneaux déjà proposés ou confirmés ne sont jamais touchés.',
  'premium.reglages.regenerer.resultat':
    '{recruteurs, plural, =0 {Aucun recruteur concerné.} one {# recruteur traité} other {# recruteurs traités}} : {crees} créé(s), {supprimes} supprimé(s), {ajustes} ajusté(s), {preserves} préservé(s).',
  'premium.reglages.regenerer.preserves': 'Créneaux préservés (déjà proposés ou confirmés) :',
  'premium.reglages.regenerer.erreur.date': 'La date de l’événement n’est pas connue : renseignez-la dans la configuration de l’événement.',
  'premium.reglages.regenerer.erreur.grille': 'Les réglages ne produisent aucun créneau : vérifiez la plage horaire et la durée.',
  'premium.reglages.regenerer.erreur': 'La régénération n’a pas abouti.',
  'premium.reglages.salles': 'Salles d’entretien',
  'premium.reglages.salles.aide':
    'Le pool commun : la salle est attribuée automatiquement à chaque entretien confirmé. Les horaires vides reprennent la plage des créneaux.',
  'premium.reglages.salles.vide': 'Aucune salle déclarée.',
  'premium.reglages.salles.nom': 'Nom',
  'premium.reglages.salles.capacite': 'Entretiens simultanés',
  'premium.reglages.salles.ouvreA': 'Ouvre à',
  'premium.reglages.salles.fermeA': 'Ferme à',
  'premium.reglages.salles.actif': 'Active',
  'premium.reglages.salles.ajouter': 'Ajouter une salle',
  'premium.reglages.salles.enregistrer': 'Enregistrer la salle',
  'premium.reglages.salles.supprimer': 'Supprimer',
  'premium.reglages.salles.supprimer.confirmer': 'Supprimer la salle « {nom} » ?',
  'premium.reglages.salles.toast.ok': 'Salle enregistrée.',
  'premium.reglages.salles.toast.supprimee': 'Salle supprimée.',
  'premium.reglages.salles.toast.erreur': 'La salle n’a pas pu être enregistrée.',
  'premium.reglages.salles.indisponible': 'Les salles se déclarent une fois le module disponible.',

  // ── Vague 1 : recruteurs (exposant) ──────────────────────────────────
  'monStand.col.scannedBy': 'Par',
  'recruteur.page.titre': 'Mes recruteurs',
  'recruteur.page.sousTitre':
    'Les personnes de {entreprise} qui scannent et rencontrent les candidats. Chacune se connecte avec son adresse et un code à usage unique.',
  'recruteur.page.retour': 'Mon stand',
  'recruteur.page.chargement': 'Chargement des recruteurs.',
  'recruteur.page.erreur': 'Les recruteurs n’ont pas pu être chargés.',
  'recruteur.page.reserve': 'Cette page est réservée au compte principal de l’entreprise.',
  'recruteur.page.compteur': '{actifs} / {max} recruteurs actifs (vous compris)',
  'recruteur.liste.vide': 'Aucun recruteur déclaré pour l’instant.',
  'recruteur.liste.mere': 'Compte principal',
  'recruteur.liste.actif': 'Actif',
  'recruteur.liste.inactif': 'Désactivé',
  'recruteur.liste.inviteLe': 'Invité le {date}',
  'recruteur.liste.jamaisInvite': 'Invitation non envoyée',
  'recruteur.liste.col.nom': 'Nom',
  'recruteur.liste.col.fonction': 'Fonction',
  'recruteur.liste.col.email': 'Courriel',
  'recruteur.liste.col.etat': 'État',
  'recruteur.liste.col.actions': 'Actions',
  'recruteur.action.desactiver': 'Désactiver',
  'recruteur.action.reactiver': 'Réactiver',
  'recruteur.action.renvoyer': 'Renvoyer l’invitation',
  'recruteur.action.badge': 'Badge',
  'recruteur.ajout.titre': 'Déclarer un recruteur',
  'recruteur.ajout.prenom': 'Prénom',
  'recruteur.ajout.nom': 'Nom',
  'recruteur.ajout.fonction': 'Fonction',
  'recruteur.ajout.email': 'Courriel',
  'recruteur.ajout.telephone': 'Téléphone (facultatif)',
  'recruteur.ajout.envoyer': 'Déclarer et inviter',
  'recruteur.ajout.plafond': 'Le nombre maximal de recruteurs est atteint ({max}, vous compris).',
  'recruteur.toast.cree': '{prenom} {nom} est déclaré et invité par courriel.',
  'recruteur.toast.creeSansMail': '{prenom} {nom} est déclaré, mais le courriel n’est pas parti : renvoyez l’invitation.',
  'recruteur.toast.emailPris': 'Cette adresse est déjà associée à un compte : utilisez une autre adresse.',
  'recruteur.toast.invalide': 'Vérifiez le prénom, le nom et le courriel.',
  'recruteur.toast.plafond': 'Le nombre maximal de recruteurs est atteint.',
  'recruteur.toast.desactive': 'Accès désactivé.',
  'recruteur.toast.reactive': 'Accès réactivé.',
  'recruteur.toast.renvoye': 'Invitation renvoyée.',
  'recruteur.toast.erreur': 'L’action n’a pas abouti. Réessayez.',
  'recruteur.badge.aide': 'Le badge d’un recruteur s’imprime depuis son propre compte (Mon badge), ou par l’organisation depuis Badges.',
  // ── Vague 2 : écran Premium de l'espace exposant, onglet Trouver ──────
  // Les onglets Entretiens (vague 3) et Recruteurs (vague 1) sont annoncés
  // « bientôt » : un onglet vide sans explication passerait pour une panne.
  'cvm.premium.titre': 'Premium',
  'cvm.premium.sousTitre':
    'Cherchez, dans les CV que les candidats vous ont ouverts, ceux qui correspondent à vos offres. Chaque recommandation est expliquée, chaque consultation est journalisée et visible du candidat.',
  'cvm.premium.retour': 'Mon stand',
  'cvm.premium.verification': 'Vérification de votre session…',
  'cvm.premium.chargement': 'Chargement…',
  'cvm.premium.erreur': 'Le module premium n’a pas pu être chargé. Réessayez.',
  'cvm.premium.notExhibitor': 'Cet espace est réservé aux {lexExhibitorPlural}.',
  'cvm.premium.onglet.trouver': 'Trouver',
  'cvm.premium.onglet.profils': 'Profils recherchés',
  'cvm.premium.onglet.entretiens': 'Entretiens',
  'cvm.premium.onglet.recruteurs': 'Recruteurs',
  'cvm.premium.bientot.entretiens':
    'Les entretiens arrivent dans une prochaine version : proposition d’un créneau de 20 minutes, réponse du candidat sous 24 heures, salle attribuée automatiquement.',
  'cvm.premium.bientot.recruteurs':
    'La déclaration de vos recruteurs (comptes rattachés, accès et badge propres) arrive dans une prochaine version.',
  'cvm.premium.bientot.titre': 'Bientôt',

  // États vides honnêtes.
  'cvm.premium.indisponible.titre': 'Module premium indisponible',
  'cvm.premium.indisponible.desactive':
    'L’organisation n’a pas activé le module premium sur {eventName}. La recherche de CV, les profils recherchés et les entretiens ne sont pas proposés.',
  'cvm.premium.indisponible.type':
    'Le module premium n’existe que sur un salon de recrutement. {eventName} n’en est pas un.',
  'cvm.premium.ficheManquante.titre': 'Créez d’abord votre fiche entreprise',
  'cvm.premium.ficheManquante.texte':
    'Le module premium s’appuie sur votre fiche : nom de l’entreprise, secteur, offres. Elle n’existe pas encore.',
  'cvm.premium.ficheManquante.lien': 'Créer ma fiche',
  'cvm.premium.nonPremium.titre': 'Votre entreprise n’est pas premium',
  'cvm.premium.nonPremium.texte':
    'La recherche de CV est réservée aux entreprises premium {eventTypeOf}. Vous pouvez préparer vos profils recherchés dès maintenant ; la recherche s’ouvrira quand l’organisation aura validé votre demande.',
  'cvm.premium.nonPremium.lien': 'Demander à passer premium depuis Mon stand',

  // Onglet Trouver : la source.
  'cvm.trouver.source': 'Chercher à partir de',
  'cvm.trouver.source.offres': 'Mes offres',
  'cvm.trouver.source.profils': 'Mes profils recherchés',
  'cvm.trouver.source.libre': 'Recherche libre',
  'cvm.trouver.choisirOffre': 'Choisissez une offre',
  'cvm.trouver.choisirProfil': 'Choisissez un profil recherché',
  'cvm.trouver.aucuneOffre': 'Vous n’avez pas encore d’offre publiée. Ajoutez-en dans Ma fiche, ou utilisez la recherche libre.',
  'cvm.trouver.aucunProfil': 'Vous n’avez pas encore de profil recherché. Créez-en un dans l’onglet Profils recherchés.',
  'cvm.trouver.libre.placeholder': 'Par exemple : soudure TIG, lecture de plans, anglais',
  'cvm.trouver.libre.aide': 'Écrivez les compétences, le métier ou les mots qui comptent. Les CV qui les contiennent remontent en premier.',
  'cvm.trouver.lancer': 'Chercher',
  'cvm.trouver.enCours': 'Recherche en cours…',
  'cvm.trouver.erreur': 'La recherche a échoué. Réessayez.',
  'cvm.trouver.vide.titre': 'Aucun CV ne correspond',
  'cvm.trouver.vide.texte':
    '{visibles, plural, =0 {Aucun candidat ne vous a ouvert son CV pour le moment.} one {Un seul CV vous est ouvert, et il ne correspond pas à ce critère.} other {# CV vous sont ouverts, aucun ne correspond à ce critère.}} Les candidats choisissent eux-mêmes qui peut voir leur CV.',
  'cvm.trouver.resume':
    '{count, plural, one {# candidat} other {# candidats}} sur {visibles} CV ouverts, classement par {moteur, select, ia {intelligence artificielle} other {règles}}.',
  'cvm.trouver.critere': 'Critère : {titre}',
  'cvm.trouver.moteur.regles': 'règles',
  'cvm.trouver.moteur.ia': 'IA',

  // Une carte de résultat.
  'cvm.carte.score': 'Score',
  'cvm.carte.scoreSur': '/100',
  'cvm.carte.pourquoi': 'Pourquoi ce CV ?',
  'cvm.carte.motsCommuns': 'Mots-clés en commun',
  'cvm.carte.nonIndexe': 'CV pas encore lu : score sur le profil déclaré seulement.',
  'cvm.carte.aCandidate': 'A candidaté chez vous',
  'cvm.carte.voirCv': 'Voir le CV',
  'cvm.carte.coordonnees': 'Coordonnées',
  'cvm.carte.coordonnees.enCours': 'Consultation…',
  'cvm.carte.coordonnees.erreur': 'Les coordonnées n’ont pas pu être consultées.',
  'cvm.carte.coordonnees.mineur': 'Candidat mineur sans accord parental : coordonnées non communiquées.',
  'cvm.carte.coordonnees.aucune': 'Aucune coordonnée renseignée.',
  'cvm.carte.coordonnees.note': 'Cette consultation est inscrite dans le journal du candidat.',
  'cvm.carte.ecarter': 'Écarter',
  'cvm.carte.retenir': 'Retenir',
  'cvm.carte.entretien': 'Proposer un entretien',
  'cvm.carte.entretien.bientot': 'Disponible dans une prochaine version (entretiens).',
  'cvm.carte.statut.nouveau': 'Nouveau',
  'cvm.carte.statut.vu': 'Vu',
  'cvm.carte.statut.ecarte': 'Écarté',
  'cvm.carte.statut.retenu': 'Retenu',
  'cvm.carte.statut.erreur': 'Le statut n’a pas pu être enregistré.',

  // Onglet Profils recherchés.
  'cvm.profils.titre': 'Profils recherchés',
  'cvm.profils.intro':
    'Décrivez ce que vous cherchez au-delà de vos offres publiées. Chaque profil actif devient une source de recherche.',
  'cvm.profils.nouveau': 'Nouveau profil',
  'cvm.profils.vide': 'Aucun profil recherché pour le moment.',
  'cvm.profils.chargement': 'Chargement des profils…',
  'cvm.profils.erreur': 'Les profils n’ont pas pu être chargés.',
  'cvm.profils.champ.intitule': 'Intitulé',
  'cvm.profils.champ.description': 'Description',
  'cvm.profils.champ.contrat': 'Type de contrat',
  'cvm.profils.champ.experience': 'Niveau d’expérience',
  'cvm.profils.champ.secteur': 'Secteur',
  'cvm.profils.champ.competences': 'Compétences attendues',
  'cvm.profils.champ.competences.aide': 'Une par ligne, ou séparées par des virgules.',
  'cvm.profils.champ.actif': 'Profil actif (proposé dans la recherche)',
  'cvm.profils.champ.indifferent': 'Indifférent',
  'cvm.profils.enregistrer': 'Enregistrer',
  'cvm.profils.annuler': 'Annuler',
  'cvm.profils.modifier': 'Modifier',
  'cvm.profils.supprimer': 'Supprimer',
  'cvm.profils.supprimer.confirmer': 'Supprimer ce profil recherché ? Les résultats calculés à partir de lui disparaissent aussi.',
  'cvm.profils.enregistre': 'Profil recherché enregistré.',
  'cvm.profils.supprime': 'Profil recherché supprimé.',
  'cvm.profils.erreurEnregistrement': 'Le profil n’a pas pu être enregistré.',
  'cvm.profils.intituleObligatoire': 'L’intitulé est obligatoire.',
  'cvm.profils.inactif': 'Inactif',
  'cvm.profils.chercher': 'Chercher avec ce profil',

  // ── Vague 2 : côté candidat, « Entreprises qui ont consulté votre CV » ─
  'cvm.vues.titre': 'Entreprises qui ont consulté votre CV',
  'cvm.vues.intro':
    'Chaque entreprise qui a vu votre CV dans sa liste, l’a récupéré ou a consulté vos coordonnées apparaît ici. C’est le même journal que celui du droit d’accès, regroupé par entreprise.',
  'cvm.vues.chargement': 'Chargement…',
  'cvm.vues.erreur': 'La liste n’a pas pu être chargée.',
  'cvm.vues.reessayer': 'Réessayer',
  'cvm.vues.entrepriseInconnue': 'Entreprise non identifiée',
  'cvm.vues.consultations': '{count, plural, one {# consultation} other {# consultations}}',
  'cvm.vues.telechargements': '{count, plural, one {# téléchargement} other {# téléchargements}}',
  'cvm.vues.contacts': '{count, plural, one {coordonnées consultées # fois} other {coordonnées consultées # fois}}',
  'cvm.vues.derniere': 'Dernière fois le {date}',
  'cvm.vues.total': '{count, plural, one {# entreprise} other {# entreprises}}',
  'monProfil.cv.journal.action.contact': 'a consulté vos coordonnées',

  // ── Vague 4 : la vue fusionnee « Tout » (/exposant/journal) ───────────
  // 2026-10-08 : le titre de page valait « Tout » (le nom de la vue dans le
  // cahier des charges), repris tel quel par l'écran et par l'onglet du
  // navigateur (titresDeRoute). Un vrai titre, unique ; un seul état vide,
  // qui dit quoi attendre, et un autre quand ce sont les filtres qui vident
  // la liste. Mikael - chef de projet - 2026-10-08
  'journal.titre': 'Activité de votre stand',
  'journal.sousTitre':
    'Tout ce qui s’est passé pour votre entreprise, du plus récent au plus ancien : scans, coups de cœur, CV reçus, recherches, entretiens. Chaque ligne indique qui a agi.',
  'journal.retour': 'Retour à mon stand',
  'journal.lien': 'Activité du stand',
  'journal.chargement': 'Chargement de l’activité…',
  'journal.erreur': 'L’activité n’a pas pu être chargée. Vérifiez votre connexion puis rechargez la page.',
  'journal.vide.titre': 'Aucune activité pour l’instant',
  'journal.vide.texte': 'Le jour J, chaque scan, CV reçu ou entretien apparaîtra ici.',
  'journal.videFiltre.titre': 'Aucune activité pour ces filtres',
  'journal.videFiltre.texte': 'Élargissez la période ou choisissez un autre type.',
  'journal.videFiltre.toutAfficher': 'Tout afficher',
  'journal.filtre.periode': 'Période',
  'journal.filtre.periode.aujourdhui': 'Aujourd’hui',
  'journal.filtre.periode.jourj': 'Jour J',
  'journal.filtre.periode.tout': 'Tout',
  'journal.filtre.type': 'Type',
  'journal.filtre.type.tous': 'Tous les types',
  'journal.filtre.recruteur': 'Recruteur',
  'journal.filtre.recruteur.tous': 'Toute l’entreprise',
  'journal.filtre.recruteur.moi': ' (moi)',
  'journal.total': '{count, plural, =0 {Aucune ligne} one {# ligne} other {# lignes}}',
  'journal.page': 'Page {page} sur {pages}',
  'journal.page.precedente': 'Précédente',
  'journal.page.suivante': 'Suivante',
  'journal.ouvrir': 'Ouvrir',
  'journal.par': 'par {acteur}',
  'journal.parCandidat': 'par le candidat',
  'journal.parSysteme': 'automatique',
  'journal.type.scan': 'Scan',
  'journal.type.rencontre': 'Rencontre',
  'journal.type.coup_de_coeur': 'Coup de cœur',
  'journal.type.cv_recu': 'CV reçu',
  'journal.type.cv_consulte': 'CV consulté',
  'journal.type.coordonnees': 'Coordonnées consultées',
  'journal.type.recherche': 'Recherche',
  'journal.type.match': 'Match',
  'journal.type.proposition': 'Proposition d’entretien',
  'journal.type.reponse': 'Réponse du candidat',
  'journal.type.entretien': 'Entretien',
  'journal.type.evaluation': 'Évaluation',
  'journal.type.note': 'Note',
  'journal.detail.etoiles': '{n} sur 5',
  'journal.detail.poste': 'Poste : {poste}',
  'journal.detail.recherche': 'Recherche {source} : {n, plural, =0 {aucun résultat} one {# résultat} other {# résultats}}',
  'journal.detail.recherche.libre': 'libre',
  'journal.detail.match.retenu': 'Retenu ({score} sur 100)',
  'journal.detail.match.ecarte': 'Écarté ({score} sur 100)',
  'journal.detail.creneau': 'Créneau {heure}',
  'journal.detail.creneauRecruteur': 'Créneau {heure} avec {recruteur}',
  'journal.detail.salle': 'salle {salle}',
  'journal.detail.reponse.accepte': 'a accepté',
  'journal.detail.reponse.refuse': 'a refusé',
  'journal.detail.reponse.contre_propose': 'propose un autre créneau',
  'journal.detail.issue.termine': 'Entretien terminé',
  'journal.detail.issue.absent': 'Candidat absent',
  'journal.detail.issue.annule': 'Entretien annulé',
  'journal.detail.issue.expire': 'Proposition expirée, créneau libéré',
  'journal.detail.evaluation': '{note} sur 5, {decision}',
  'journal.detail.note': 'Note ajoutée à l’entretien',
  'journal.candidat.inconnu': 'un candidat',

  // ── Vague 4 : la fiche complete, sections Premium et Entretiens ──────
  'cvm.fiche.premium.titre': 'Premium',
  'cvm.fiche.premium.statut': 'Statut premium',
  'cvm.fiche.premium.statut.aucun': 'Aucun',
  'cvm.fiche.premium.statut.demande': 'Demande en attente',
  'cvm.fiche.premium.statut.actif': 'Actif',
  'cvm.fiche.premium.statut.refuse': 'Refusé',
  'cvm.fiche.premium.statut.retire': 'Retiré',
  'cvm.fiche.premium.moduleIndisponible': 'Module premium indisponible sur cette instance',
  'cvm.fiche.premium.role': 'Rôle du compte',
  'cvm.fiche.premium.role.mere': 'Compte principal',
  'cvm.fiche.premium.role.recruteur': 'Recruteur rattaché',
  'cvm.fiche.premium.role.inactif': ' (accès désactivé)',
  'cvm.fiche.premium.plafond': 'Plafond d’entretiens',
  'cvm.fiche.premium.recherches': 'Recherches lancées',
  'cvm.fiche.premium.proposes': 'Entretiens proposés',
  'cvm.fiche.premium.menes': 'Entretiens menés',
  'cvm.fiche.premium.recruteurs': 'Recruteurs',
  'cvm.fiche.premium.recruteurs.col.nom': 'Nom',
  'cvm.fiche.premium.recruteurs.col.fonction': 'Fonction',
  'cvm.fiche.premium.recruteurs.col.email': 'E-mail',
  'cvm.fiche.premium.recruteurs.col.etat': 'État',
  'cvm.fiche.premium.recruteurs.mere': 'principal',
  'cvm.fiche.premium.recruteurs.actif': 'actif',
  'cvm.fiche.premium.recruteurs.inactif': 'désactivé',
  'cvm.fiche.premium.entretiens': 'Entretiens',
  'cvm.fiche.premium.entretiens.col.quand': 'Quand',
  'cvm.fiche.premium.entretiens.col.candidat': 'Candidat',
  'cvm.fiche.premium.entretiens.col.recruteur': 'Recruteur',
  'cvm.fiche.premium.entretiens.col.statut': 'Statut',
  'cvm.fiche.premium.entretiens.col.salle': 'Salle',
  'cvm.fiche.premium.entretiens.col.evaluation': 'Évaluation',
  'cvm.fiche.premium.entretiens.evaluation': '{note} sur 5, {decision}',
  'cvm.fiche.premium.retenus': 'Candidats retenus',
  'cvm.fiche.premium.retenus.col.candidat': 'Candidat',
  'cvm.fiche.premium.retenus.col.source': 'Source',
  'cvm.fiche.premium.retenus.col.score': 'Score',
  'cvm.fiche.premium.retenus.col.quand': 'Retenu le',
  'cvm.fiche.candidat.titre': 'Entretiens et CV',
  'cvm.fiche.candidat.visibilite': 'Ouverture du CV',
  'cvm.fiche.candidat.visibilite.scanned': 'Entreprises qui l’ont scanné',
  'cvm.fiche.candidat.visibilite.premium': 'Entreprises premium',
  'cvm.fiche.candidat.visibilite.all': 'Toutes les entreprises',
  'cvm.fiche.candidat.visibilite.le': 'Choisi le',
  'cvm.fiche.candidat.entretiens': 'Entretiens',
  'cvm.fiche.candidat.entretiens.col.quand': 'Quand',
  'cvm.fiche.candidat.entretiens.col.entreprise': 'Entreprise',
  'cvm.fiche.candidat.entretiens.col.recruteur': 'Recruteur',
  'cvm.fiche.candidat.entretiens.col.statut': 'Statut',
  'cvm.fiche.candidat.entretiens.col.salle': 'Salle',
  'cvm.fiche.candidat.consultations': 'Entreprises ayant consulté le CV',
  'cvm.fiche.candidat.consultations.col.entreprise': 'Entreprise',
  'cvm.fiche.candidat.consultations.col.vues': 'Consultations',
  'cvm.fiche.candidat.consultations.col.contacts': 'Coordonnées',
  'cvm.fiche.candidat.consultations.col.derniere': 'Dernière fois',
  'cvm.fiche.candidat.consultations.inconnue': 'Entreprise non identifiée',
  'cvm.fiche.candidat.interne': 'Les évaluations des entreprises sont internes à chacune : elles ne figurent pas ici.',

  // ── Vague 4 : Ma fiche en lecture seule pour un recruteur ────────────
  'recruteur.lectureSeule.mention': 'Seul le compte principal modifie la fiche.',
  'recruteur.lectureSeule.vide': 'Non renseigné',
  'recruteur.lectureSeule.oui': 'Oui',
  'recruteur.lectureSeule.non': 'Non',
  'recruteur.lectureSeule.postes': 'Postes à pourvoir',
  'recruteur.lectureSeule.postes.vide': 'Aucun poste déclaré.',
  'recruteur.lectureSeule.poste.publie': 'publié',
  'recruteur.lectureSeule.poste.brouillon': 'brouillon',

  // ── Vague 4 : colonne « Par » (qui a fait l action) ──────────────────
  'cvm.par.col': 'Par',
  'cvm.par.moi': 'moi',
};
